import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getMdsPatients,
  MdsPatientListResponse,
  MdsPatientListParams,
  MdsSortOption,
} from 'src/services/documentService/getMdsPatients';

/**
 * BRK-1851: list-page state + fetching for the MDS patient list. Search is
 * debounced 1.5s (house convention, see patients.tsx); every other filter
 * change refetches immediately and resets to page 0. Stale responses from
 * out-of-order fetches are dropped via a request counter.
 */

export const SEARCH_DEBOUNCE_MS = 1500;
export const DEFAULT_PAGE_SIZE = 25;

export type MdsPatientFilters = {
  search: string;
  active: boolean;
  inactive: boolean;
  needsAttentionOnly: boolean;
  sort: MdsSortOption;
  page: number;
};

const INITIAL_FILTERS: MdsPatientFilters = {
  search: '',
  active: true,
  inactive: false,
  needsAttentionOnly: false,
  sort: 'name_asc',
  page: 0,
};

export function useMdsPatients(facilityUuid: string) {
  const [filters, setFilters] = useState<MdsPatientFilters>(INITIAL_FILTERS);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [data, setData] = useState<MdsPatientListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestSeq = useRef(0);
  const lastFetchedSearch = useRef('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    if (!facilityUuid) {
      return;
    }
    // a freshly debounced search must land on page 0 — reset (without
    // fetching) and let the page change re-run this effect exactly once
    if (debouncedSearch !== lastFetchedSearch.current) {
      lastFetchedSearch.current = debouncedSearch;
      if (filters.page !== 0) {
        setFilters((prev) => ({ ...prev, page: 0 }));
        return;
      }
    }
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    const params: MdsPatientListParams = {
      facilityUuid,
      active: filters.active,
      inactive: filters.inactive,
      search: debouncedSearch,
      needsAttentionOnly: filters.needsAttentionOnly,
      sort: filters.sort,
      page: filters.page,
      pageSize: DEFAULT_PAGE_SIZE,
    };
    getMdsPatients(params)
      .then((response) => {
        if (seq === requestSeq.current) {
          setData(response);
          setLoading(false);
        }
      })
      .catch((e: Error) => {
        if (seq === requestSeq.current) {
          setError(e.message);
          setData(null);
          setLoading(false);
        }
      });
  }, [
    facilityUuid,
    debouncedSearch,
    filters.active,
    filters.inactive,
    filters.needsAttentionOnly,
    filters.sort,
    filters.page,
  ]);

  const updateFilter = useCallback(
    (patch: Partial<MdsPatientFilters>) => {
      // non-paging filter changes return to the first page — EXCEPT raw
      // search keystrokes: their page reset happens when the debounced value
      // commits, otherwise typing on page >0 fires an immediate fetch with
      // the stale (pre-debounce) search text
      const searchOnly = Object.keys(patch).length === 1 && 'search' in patch;
      setFilters((prev) => ({
        ...prev,
        ...patch,
        page: 'page' in patch ? (patch.page as number) : searchOnly ? prev.page : 0,
      }));
    },
    [setFilters]
  );

  return { filters, updateFilter, data, loading, error };
}
